<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { offeredVisitDates } from '../visitTask';

// Schedules a visit request: one of the dates the customer offered, or another one, and the
// agent who shows the property.
const props = defineProps({
  agents: { type: Array, required: true },
  // Resolves to whether the visit was scheduled; the dialog closes only then.
  submit: { type: Function, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

// ComboBox needs a truthy value for every option.
const NOBODY = 'none';
const OTHER = 'other';

const dialogRef = ref(null);
const offered = ref([]);
// The key of an offered date, or OTHER.
const choice = ref(OTHER);
const otherDate = ref('');
const assigneeId = ref(NOBODY);
const isSaving = ref(false);

const agentOptions = computed(() => [
  { value: NOBODY, label: t('CARDS.TASKS.UNASSIGNED') },
  ...props.agents.map(agent => ({ value: agent.id, label: agent.name })),
]);

// A datetime-local value: the browser's wall-clock time.
const scheduledAt = computed(() =>
  choice.value === OTHER
    ? otherDate.value
    : offered.value.find(date => date.key === choice.value)?.value
);

const open = task => {
  offered.value = offeredVisitDates(task);
  choice.value = offered.value[0]?.key || OTHER;
  otherDate.value = '';
  assigneeId.value = task.assignee?.id || NOBODY;
  dialogRef.value.open();
};

const onConfirm = async () => {
  isSaving.value = true;
  const scheduled = await props.submit({
    scheduledAt: new Date(scheduledAt.value).toISOString(),
    assigneeId: assigneeId.value === NOBODY ? undefined : assigneeId.value,
  });
  isSaving.value = false;
  if (scheduled) dialogRef.value.close();
};

const formatOption = value => format(new Date(value), "dd/MM/yyyy 'às' HH:mm");

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="$t('CARDS.VISIT.SCHEDULE')"
    :confirm-button-label="$t('CARDS.VISIT.CONFIRM')"
    :disable-confirm-button="!scheduledAt || isSaving"
    :is-loading="isSaving"
    @confirm="onConfirm"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span class="text-sm text-n-slate-12">
          {{ $t('CARDS.VISIT.DATE') }}
        </span>
        <button
          v-for="date in offered"
          :key="date.key"
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg outline outline-1 -outline-offset-1"
          :class="
            choice === date.key
              ? 'outline-n-blue-9 bg-n-blue-2 text-n-slate-12'
              : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          :data-visit-option="date.key"
          @click="choice = date.key"
        >
          <span class="i-lucide-calendar size-4" />
          {{ formatOption(date.value) }}
          <span class="text-xs text-n-slate-10 ltr:ml-auto rtl:mr-auto">
            {{ $t('CARDS.VISIT.OFFERED') }}
          </span>
        </button>
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg outline outline-1 -outline-offset-1"
          :class="
            choice === 'other'
              ? 'outline-n-blue-9 bg-n-blue-2 text-n-slate-12'
              : 'outline-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          data-visit-option="other"
          @click="choice = 'other'"
        >
          <span class="i-lucide-calendar-plus size-4" />
          {{ $t('CARDS.VISIT.OTHER_DATE') }}
        </button>
        <Input
          v-if="choice === 'other'"
          v-model="otherDate"
          type="datetime-local"
          data-testid="visit-other-date"
        />
      </div>
      <div class="flex flex-col gap-1" data-testid="visit-assignee">
        <span class="text-sm text-n-slate-12">
          {{ $t('CARDS.VISIT.ASSIGNEE') }}
        </span>
        <ComboBox v-model="assigneeId" :options="agentOptions" />
      </div>
    </div>
  </Dialog>
</template>
